import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Shield, Award, Users, CheckCircle, ArrowRight, Star, Search, Filter, ExternalLink } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { CARRIERS, CATEGORY_LABELS, RATING_LABELS, type Carrier } from '@/data/carriers';

const Carriers = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [ratingFilter, setRatingFilter] = useState('all');

  // Filter carriers based on search and filters
  const filteredCarriers = useMemo(() => {
    return CARRIERS.filter(carrier => {
      const matchesSearch = carrier.name.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesCategory = categoryFilter === 'all' || carrier.categories.includes(categoryFilter as any);
      const matchesRating = ratingFilter === 'all' || carrier.amBestRating === ratingFilter;
      
      return matchesSearch && matchesCategory && matchesRating;
    });
  }, [searchTerm, categoryFilter, ratingFilter]);

  // Group carriers by category
  const groupedCarriers = useMemo(() => {
    const groups: Record<string, Carrier[]> = {
      term: [],
      permanent: [],
      no_exam: [],
      final_expense: []
    };

    filteredCarriers.forEach(carrier => {
      carrier.categories.forEach(category => {
        if (groups[category]) {
          groups[category].push(carrier);
        }
      });
    });

    return groups;
  }, [filteredCarriers]);

  const selectionCriteria = [
    {
      icon: Shield,
      title: "Financial Strength",
      description: "All carriers must have A- rating or higher from A.M. Best"
    },
    {
      icon: Users,
      title: "Customer Service",
      description: "Proven track record of excellent customer satisfaction scores"
    },
    {
      icon: Award,
      title: "Claims Payment",
      description: "Strong history of paying claims promptly and fairly"
    },
    {
      icon: CheckCircle,
      title: "Product Quality",
      description: "Competitive rates and flexible policy options"
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-b from-primary-light/30 to-background">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-6xl font-bold text-foreground mb-6">
              Our Insurance Partners
            </h1>
            <p className="text-xl md:text-2xl text-muted-foreground mb-8 max-w-3xl mx-auto">
              We work with trusted national life insurance providers to match you with 
              the best possible option for your needs.
            </p>
            <Link to="/">
              <Button size="lg" variant="hero">
                Get Matched Now
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Selection Criteria */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-6">
                How We Choose Our Partners
              </h2>
              <p className="text-xl text-muted-foreground">
                We only work with carriers that meet our strict standards for financial stability, 
                customer service, and product quality.
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {selectionCriteria.map((criteria, index) => (
                <Card key={index} className="text-center p-6 hover:shadow-lg transition-shadow">
                  <CardContent className="p-0">
                    <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                      <criteria.icon className="h-8 w-8 text-primary" />
                    </div>
                    <h3 className="text-lg font-semibold text-foreground mb-3">
                      {criteria.title}
                    </h3>
                    <p className="text-muted-foreground text-sm">
                      {criteria.description}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Search and Filter */}
      <section className="py-12 bg-primary-light/30">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-6">
                Our Insurance Partners
              </h2>
              <p className="text-xl text-muted-foreground mb-8">
                Browse our trusted network of top-rated life insurance carriers.
              </p>
            </div>
            
            {/* Filter Controls */}
            <div className="flex flex-col md:flex-row gap-4 mb-8">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search carriers..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                <SelectTrigger className="w-full md:w-48">
                  <SelectValue placeholder="Category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  <SelectItem value="term">Term Life</SelectItem>
                  <SelectItem value="permanent">Permanent</SelectItem>
                  <SelectItem value="no_exam">No-Exam</SelectItem>
                  <SelectItem value="final_expense">Final Expense</SelectItem>
                </SelectContent>
              </Select>
              <Select value={ratingFilter} onValueChange={setRatingFilter}>
                <SelectTrigger className="w-full md:w-32">
                  <SelectValue placeholder="Rating" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Ratings</SelectItem>
                  <SelectItem value="A++">A++</SelectItem>
                  <SelectItem value="A+">A+</SelectItem>
                  <SelectItem value="A">A</SelectItem>
                  <SelectItem value="A-">A-</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Active Filters */}
            {(searchTerm || categoryFilter !== 'all' || ratingFilter !== 'all') && (
              <div className="flex flex-wrap gap-2 mb-6">
                {searchTerm && (
                  <Badge variant="secondary" className="gap-1">
                    Search: {searchTerm}
                    <button onClick={() => setSearchTerm('')} className="ml-1 hover:text-foreground">×</button>
                  </Badge>
                )}
                {categoryFilter !== 'all' && (
                  <Badge variant="secondary" className="gap-1">
                    Category: {CATEGORY_LABELS[categoryFilter as keyof typeof CATEGORY_LABELS]}
                    <button onClick={() => setCategoryFilter('all')} className="ml-1 hover:text-foreground">×</button>
                  </Badge>
                )}
                {ratingFilter !== 'all' && (
                  <Badge variant="secondary" className="gap-1">
                    Rating: {ratingFilter}
                    <button onClick={() => setRatingFilter('all')} className="ml-1 hover:text-foreground">×</button>
                  </Badge>
                )}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Carrier Categories */}
      <section className="py-12">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="space-y-12">
              {Object.entries(groupedCarriers).map(([category, carriers]) => {
                if (carriers.length === 0) return null;
                
                return (
                  <div key={category}>
                    <h3 className="text-2xl font-bold text-foreground mb-6">
                      {CATEGORY_LABELS[category as keyof typeof CATEGORY_LABELS]}
                    </h3>
                    <div className="grid md:grid-cols-2 gap-4">
                      {carriers.map((carrier) => (
                        <TooltipProvider key={carrier.slug}>
                          <Card className="group hover:shadow-lg transition-all duration-200 cursor-pointer border-border hover:border-primary/30">
                            <Link to={`/quote?carrier=${carrier.slug}`} className="block">
                              <CardContent className="p-6">
                                <div className="flex items-center gap-4">
                                   {/* Logo */}
                                   <div className="flex-shrink-0 w-20 h-16 flex items-center justify-center">
                                     <img
                                       src={carrier.logo.light}
                                       alt={carrier.logo.alt}
                                       className="max-w-full max-h-full object-contain"
                                       loading="lazy"
                                       style={{ width: 'auto', height: 'auto' }}
                                     />
                                   </div>
                                  
                                  {/* Content */}
                                  <div className="flex-grow min-w-0">
                                    <div className="flex items-start justify-between gap-2">
                                      <div>
                                        <h4 className="font-semibold text-foreground group-hover:text-primary transition-colors">
                                          {carrier.name}
                                        </h4>
                                        {carrier.strengths && carrier.strengths[0] && (
                                          <p className="text-sm text-muted-foreground mt-1">
                                            {carrier.strengths[0]}
                                          </p>
                                        )}
                                      </div>
                                      
                                      {/* Rating */}
                                      {carrier.amBestRating && (
                                        <Tooltip>
                                          <TooltipTrigger asChild>
                                            <div className="flex items-center gap-1 flex-shrink-0">
                                              <Star className="h-4 w-4 text-yellow-500 fill-current" />
                                              <span className="text-sm font-medium text-foreground">
                                                {carrier.amBestRating}
                                              </span>
                                            </div>
                                          </TooltipTrigger>
                                          <TooltipContent>
                                            <p>A.M. Best financial strength rating: {RATING_LABELS[carrier.amBestRating]}</p>
                                          </TooltipContent>
                                        </Tooltip>
                                      )}
                                    </div>
                                  </div>
                                </div>
                                
                                {/* Learn More Link */}
                                {carrier.website && (
                                  <div className="mt-4 pt-4 border-t border-border">
                                    <a
                                      href={carrier.website}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-primary transition-colors"
                                      onClick={(e) => e.stopPropagation()}
                                      aria-label={`Learn more about ${carrier.name}`}
                                    >
                                      {carrier.slug === 'ladder-life' ? 'Get Quote' : 'Learn more'}
                                      <ExternalLink className="h-3 w-3" />
                                    </a>
                                  </div>
                                )}
                              </CardContent>
                            </Link>
                          </Card>
                        </TooltipProvider>
                      ))}
                    </div>
                  </div>
                );
              })}
              
              {filteredCarriers.length === 0 && (
                <div className="text-center py-12">
                  <p className="text-lg text-muted-foreground mb-4">No carriers found matching your criteria.</p>
                  <Button 
                    variant="outline" 
                    onClick={() => {
                      setSearchTerm('');
                      setCategoryFilter('all');
                      setRatingFilter('all');
                    }}
                  >
                    Clear Filters
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-primary-light/30">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-6">
                Carrier Network Stats
              </h2>
              <p className="text-xl text-muted-foreground">
                Our comprehensive network ensures competitive options for every customer.
              </p>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
              <div className="space-y-2">
                <div className="text-3xl md:text-4xl font-bold text-primary">{CARRIERS.length}+</div>
                <div className="text-muted-foreground">Partner Carriers</div>
              </div>
              <div className="space-y-2">
                <div className="text-3xl md:text-4xl font-bold text-primary">A+</div>
                <div className="text-muted-foreground">Average Rating</div>
              </div>
              <div className="space-y-2">
                <div className="text-3xl md:text-4xl font-bold text-primary">$50T+</div>
                <div className="text-muted-foreground">Assets Under Management</div>
              </div>
              <div className="space-y-2">
                <div className="text-3xl md:text-4xl font-bold text-primary">98%</div>
                <div className="text-muted-foreground">Claims Paid</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Important Note */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <Card className="border-primary/20 bg-primary/5">
              <CardContent className="p-8 text-center">
                <Shield className="h-12 w-12 text-primary mx-auto mb-4" />
                <h3 className="text-2xl font-bold text-foreground mb-4">
                  Your Best Interest Comes First
                </h3>
                <p className="text-muted-foreground text-lg mb-6 max-w-2xl mx-auto">
                  We earn compensation from our carrier partners, but our recommendations are always 
                  based on what's best for your specific situation - not what pays us the most.
                </p>
                <Link to="/legal/disclosures">
                  <Button variant="outline" size="lg">
                    View Full Disclosures
                  </Button>
                </Link>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Legal Disclaimer */}
      <section className="py-8 bg-muted/30">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <p className="text-sm text-muted-foreground">
              Safurra compares quotes from multiple top-rated carriers. Logos are trademarks of their respective owners and do not imply endorsement.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-6">
              Ready to Find Your Perfect Match?
            </h2>
            <p className="text-xl text-muted-foreground mb-8">
              Let our AI analyze your needs and match you with the best carrier from our trusted network.
            </p>
            <Link to="/">
              <Button size="lg" variant="hero">
                Get Your Personalized Quote
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Carriers;